<script setup lang="ts">
  import { KEY_TAB } from '@travelata-libs/utils';
  import { vMaska } from 'maska/vue';
  import { ref, unref, computed, nextTick, watch, type InputHTMLAttributes, onMounted } from 'vue';
  import { InputType, InputSize } from './Input.types';
  import { CLEAR_BUTTON_ICON_SIZES, INNER_TEXT_VARIANT_BY_SIZE, LABEL_SIZE } from './lib';
  import { Field } from '../Field';
  import { IconSize, Typo, Button, TypoVariant } from '@/components';

  interface InputProps {
    modelValue: string;
    label?: string;
    placeholder?: string;
    type?: `${InputType}`;
    mask?: string;
    note?: string;
    error?: string | boolean;
    disabled?: boolean;
    readonly?: boolean;
    hiddenReadOnly?: boolean;
    clearable?: boolean;
    unmasked?: boolean;
    required?: boolean;
    prefix?: string;
    suffix?: string;
    size?: `${InputSize}`;
    name?: InputHTMLAttributes['name'];
    id?: InputHTMLAttributes['id'];
    maxLength?: number | null;
    bg?: boolean;
  }

  interface InputEmits {
    (e: 'update:modelValue', value: string): void;
    (e: 'change', value: string): void;
    (e: 'focus'): void;
    (e: 'blur', event: Event): void;
    (e: 'clear'): void;
    (e: 'click'): void;
  }

  const props = withDefaults(defineProps<InputProps>(), {
    label: '',
    placeholder: '',
    type: InputType.Text,
    mask: '',
    note: '',
    error: '',
    disabled: false,
    readonly: false,
    hiddenReadOnly: false,
    clearable: false,
    unmasked: true,
    required: false,
    prefix: '',
    suffix: '',
    size: InputSize.S,
    name: '',
    id: '',
    maxLength: null,
    bg: true,
  });

  const emit = defineEmits<InputEmits>();
  const MASK_CHARS_REGEXP = /[^\w\dа-яА-ЯёЁ+]/g;
  const MASK_TOKENS = {
    C: { pattern: '[а-яА-Я]' }, //кириллические символы
    $: { pattern: '[а-яА-Я0-9]' }, //кириллические символы и цифры
    '&': { pattern: '[a-zA-Zа-яА-Я]' }, //кириллические и латинские символы
    '^': { pattern: '[a-zA-Zа-яА-Я0-9]' }, //кириллические и латинские символы и цифры
  };
  const DEFAULT_PHONE_MASK = '+7 (###) ###-##-##';
  const hiddenRef = ref<HTMLElement | null>(null);
  const inputElementRef = ref<HTMLElement | null>(null);
  const fieldRef = ref<HTMLElement | null>(null);
  const isFocused = ref(false);
  const isMounted = ref(false);
  const isTabProcessed = ref(false);
  const innerError = ref('');

  const labelSize = computed(() => LABEL_SIZE[props.size]);
  const clearButtonSize = computed(() => CLEAR_BUTTON_ICON_SIZES[props.size] || IconSize.M);

  const inputMask = computed(() => {
    if (props.type === InputType.Tel && props.mask === '') {
      return DEFAULT_PHONE_MASK;
    }
    return props.mask;
  });

  const getUnmaskedValue = (value: string) => {
    return value.replaceAll(MASK_CHARS_REGEXP, '');
  };

  const getMaskPrefix = (mask: string) => mask?.split(' ')[0];

  const prepareValue = (value: string) => {
    if (props.unmasked && unref(inputMask) !== '') {
      return getUnmaskedValue(value);
    }
    return value;
  };

  const innerValue = computed({
    get() {
      return props.modelValue;
    },
    set(value: string) {
      if (value === innerValue.value || prepareValue(value) === innerValue.value) {
        // хак от двойного эммита маски https://github.com/beholdr/maska/issues/244
        return;
      }

      emit('update:modelValue', prepareValue(value));
    },
  });

  const clearButtonIsVisible = computed(
    () => props.clearable && !!innerValue.value && !props.disabled && !props.readonly,
  );

  const innerTextVariant = computed(() => INNER_TEXT_VARIANT_BY_SIZE[props.size] || TypoVariant.BodyMRegular);

  const maskPrefix = computed(() =>
    props.mask === '' ? getMaskPrefix(DEFAULT_PHONE_MASK) : getMaskPrefix(props.mask),
  );

  const leftElementsIsVisible = computed(() => !props.label || isFocused.value || !!innerValue.value);

  const focus = () => {
    inputElementRef.value?.focus();
  };

  const blur = () => {
    inputElementRef.value?.blur();
  };

  const onFocus = () => {
    fieldRef.value?.focus();

    isFocused.value = true;
    emit('focus');
  };

  const onBlur = (event: Event) => {
    fieldRef.value?.blur();

    isFocused.value = false;
    emit('blur', isTabProcessed.value ? Object.assign(event, { key: KEY_TAB }) : event);

    isTabProcessed.value = false;
  };

  const onClear = () => {
    innerValue.value = props.type === InputType.Tel ? unref(maskPrefix) : '';
    emit('clear');
  };

  const onClick = (event: Event) => {
    event.preventDefault();

    if (!isFocused.value) {
      inputElementRef.value?.focus();
    }

    emit('click');
  };

  const onChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    emit('change', prepareValue(target.value));
    inputElementRef.value?.blur();
  };

  const onPaste = (event: ClipboardEvent) => {
    if (props.readonly) {
      return;
    }
    if (event.clipboardData && props.type === InputType.Tel) {
      const pastedText = event.clipboardData.getData('text');
      const target = event.target as HTMLInputElement | null;

      if (target) {
        const currentMask = unref(maskPrefix);
        if (pastedText.startsWith(currentMask)) {
          target.value = pastedText.slice(currentMask?.length);
        } else if (pastedText.startsWith('8') || pastedText.startsWith('7')) {
          target.value = pastedText.slice(1);
        } else {
          target.value = pastedText;
        }
      }
    }
  };

  const setInputWidth = () => {
    if (inputElementRef.value && hiddenRef.value) {
      hiddenRef.value.textContent = innerValue.value === '' ? props.placeholder : innerValue.value;
      inputElementRef.value.style.width = hiddenRef.value.offsetWidth + 'px';
    }
  };

  const onBeforeInput = (e: InputEvent) => {
    if (props.type !== InputType.Tel) return;

    const el = inputElementRef.value as HTMLInputElement | null;
    if (!el) return;

    if (getUnmaskedValue(el.value) === '+7' && e.data === '8') {
      e.preventDefault();
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!props.disabled && [KEY_TAB].includes(event.key)) {
      isTabProcessed.value = true;
    }
  };

  watch(
    [() => props.suffix, () => props.required, innerValue],
    () => {
      nextTick(setInputWidth);

      if (props.required && isMounted.value) {
        innerError.value = innerValue.value === '' ? 'Поле обязательно для заполнения' : '';
      } else if (innerError.value) {
        innerError.value = '';
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    isMounted.value = true;
  });

  defineExpose({
    focus,
    blur,
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <Field
    ref="fieldRef"
    :label="label"
    :note="note"
    :size="labelSize"
    :filled="!!modelValue"
    :disabled="disabled"
    :readonly="readonly"
    :error="error || innerError"
    :required="required"
    @click="onClick"
  >
    <template #default>
      <span
        :class="[
          'tl-input',
          `tl-input_size-${size}`,
          {
            'tl-input_focused': isFocused,
            'tl-input_disabled': disabled,
            'tl-input_readonly': readonly,
            'tl-input_unlabeled': !label,
            'tl-input_error': error || innerError,
            'tl-input_bg': bg,
          },
        ]"
        data-test-id="input-wrapper"
      >
        <span v-if="suffix" ref="hiddenRef" data-test-id="hidden" class="tl-input__hidden">
          {{ innerValue || placeholder }}
        </span>

        <Transition name="fade">
          <span v-if="leftElementsIsVisible && $slots.prepend" class="tl-input__prepend">
            <slot name="prepend" />
          </span>
        </Transition>

        <Transition name="fade">
          <Typo v-if="prefix && leftElementsIsVisible" :variant="innerTextVariant" class="tl-input__prefix">
            {{ prefix }}
          </Typo>
        </Transition>

        <input
          :id="id"
          ref="inputElementRef"
          v-model="innerValue"
          v-maska="inputMask"
          :data-maska-tokens="JSON.stringify(MASK_TOKENS)"
          class="tl-input__source"
          aria-label="input"
          autocomplete="off"
          :name="name"
          :type="type"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly || hiddenReadOnly"
          :maxlength="maxLength"
          data-test-id="input"
          tabindex="0"
          @focus="onFocus"
          @blur="onBlur"
          @change="onChange"
          @paste="onPaste"
          @keydown="onKeyDown"
          @beforeinput="onBeforeInput"
        />

        <Transition name="fade">
          <Typo v-if="suffix && leftElementsIsVisible" :variant="innerTextVariant" class="tl-input__suffix">
            {{ suffix }}
          </Typo>
        </Transition>

        <span class="tl-input__right-group">
          <span v-if="$slots.append" class="tl-input__append">
            <slot name="append" />
          </span>

          <transition name="fade">
            <Button
              v-if="clearButtonIsVisible"
              appearance="tertiary"
              type="icon"
              icon="close"
              :size="clearButtonSize"
              data-test-id="clear-button"
              class="tl-input__clear"
              @click.stop.prevent="onClear"
            />
          </transition>
        </span>
      </span>
    </template>
  </Field>
</template>

<style lang="scss" scoped>
  .tl-input {
    $root: &;

    align-items: center;
    cursor: text;
    display: flex;

    &_bg {
      &:not(#{$root}_disabled) {
        background-color: var(--color-bg-primary);
      }
    }

    &_disabled {
      #{$root}__source {
        color: var(--color-text-tertiary);

        &::placeholder {
          color: var(--color-text-tertiary);
        }
      }
    }

    &_size {
      &-s {
        padding: 0 var(--spacing-xs);

        #{$root}__hidden,
        #{$root}__source {
          height: 30px;

          @include typography-body(m, 400);
        }

        &#{$root}_readonly {
          #{$root}__hidden,
          #{$root}__source {
            @include typography-body(m, 500);
          }
        }
      }

      &-m {
        padding: 0 var(--spacing-s);

        #{$root}__hidden,
        #{$root}__source {
          height: 38px;

          @include typography-body(m, 400);
        }

        &#{$root}_readonly {
          #{$root}__hidden,
          #{$root}__source {
            @include typography-body(m, 500);
          }
        }
      }

      &-l {
        padding: 0 var(--spacing-m);

        #{$root}__hidden,
        #{$root}__source {
          height: 46px;

          @include typography-body(l, 400);
        }

        &#{$root}_readonly {
          #{$root}__hidden,
          #{$root}__source {
            @include typography-body(l, 500);
          }
        }
      }

      &-xl {
        padding: 0 var(--spacing-m);

        #{$root}__hidden,
        #{$root}__source {
          height: 54px;

          @include typography-body(xl, 400);
        }

        &#{$root}_readonly {
          #{$root}__hidden,
          #{$root}__source {
            @include typography-body(xl, 500);
          }
        }
      }
    }

    &_unlabeled {
      input:not(:focus)::placeholder {
        opacity: 1;
      }
    }

    &__prepend,
    &__append {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
      text-wrap: nowrap;
    }

    &__prepend {
      margin-right: var(--spacing-xs);
    }

    &__append {
      margin-left: var(--spacing-xs);
    }

    &__prefix,
    &__suffix {
      color: var(--color-text-secondary);
      text-wrap: nowrap;
    }

    &__prefix {
      margin-right: var(--spacing-xxs);
    }

    &__suffix {
      margin-left: var(--spacing-xxs);
    }

    &__source {
      background: transparent;
      border: none;
      color: var(--color-text-primary);
      margin: 0;
      min-width: 10px;
      outline: none;
      padding: 0;
      touch-action: manipulation;
      width: 100%;

      &::placeholder {
        color: var(--color-text-secondary);
        opacity: 0;
        transition: var(--transition-default);
      }

      &:focus::placeholder {
        opacity: 1;
      }
    }

    &__clear {
      margin-left: var(--spacing-xs);
    }

    &__right-group {
      align-items: center;
      display: flex;
      margin-left: auto;
    }

    &__hidden {
      margin: 0;
      padding: 0;
      position: absolute;
      visibility: hidden;
      white-space: pre;
    }
  }
</style>
